import {
  Store as VuexStore,
  ActionContext,
  ActionTree,
  CommitOptions,
  DispatchOptions,
  GetterTree,
  Module,
  MutationTree,
} from "vuex";
import { Setting } from "@/models/setting";
import { SettingsState } from "@/store/modules/settings/settings-state";
import { RootState } from "@/store/root-state";
import settingsService from "@/services/settings-service";

// initial state
const state: SettingsState = {
  loading: false,
  settings: [] as Setting[],
};

// getters
export type Getters = {
  loading(state: SettingsState): boolean;
  settings(state: SettingsState): Setting[];
};

const getters: GetterTree<SettingsState, RootState> & Getters = {
  loading(state: SettingsState): boolean {
    return state.loading;
  },
  settings(state: SettingsState): Setting[] {
    return state.settings;
  },
};

// actions
export interface Actions {
  loadSettings({ commit }: ActionContext<SettingsState, RootState>): void;
}

const actions: ActionTree<SettingsState, RootState> = {
  loadSettings({ commit }: ActionContext<SettingsState, RootState>) {
    commit("setLoading", true);
    settingsService.getSettings().then(
      (settings) => {
        commit("setSettings", settings);
        commit("setLoading", false);
      },
      (error) => {
        commit("setLoading", false);
      }
    );
  },
};

// mutations
export type Mutations = {
  setLoading(state: SettingsState, payload: boolean): void;
  setSettings(state: SettingsState, payload: Setting[]): void;
};

const mutations: MutationTree<SettingsState> & Mutations = {
  setLoading(state: SettingsState, payload: boolean): void {
    state.loading = payload;
  },
  setSettings(state: SettingsState, payload: Setting[]): void {
    state.settings = payload;
  },
};

// store
export type SettingsStore<S = SettingsState> = Omit<VuexStore<S>, "getters" | "commit" | "dispatch"> & {
  commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
    key: K,
    payload: P,
    options?: CommitOptions
  ): ReturnType<Mutations[K]>;
} & {
  // TODO: How to configure a module without any actions?
  dispatch<K extends keyof Actions>(
    key: K,
    payload: Parameters<Actions[K]>[1],
    options?: DispatchOptions
  ): ReturnType<Actions[K]>;
} & {
  getters: {
    [K in keyof Getters]: ReturnType<Getters[K]>;
  };
};

export const settings: Module<SettingsState, RootState> = {
  state,
  getters,
  actions,
  mutations,
};
